import { useAtom, useAtomValue } from 'jotai';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Card, Button, InputDeprecated } from '@/components';
import { R } from '@/features/booking/resources/Texts.resx';
import { isPhoneNumber } from '@/shared/validators';
import { phoneNumberAtom } from '@/features/booking/shared/model/bookingState';
import { IUserDto, ProductDto } from '@/shared/interfaces';
import { useCreateConsultation } from '@/features/booking/shared/model/useCreateConsultation';
import { Log } from '@/shared/Log';
import { isSubmittingConsultationAtom } from '@/features/booking/shared/model/latestActions';

type FormData = {
    phoneNumber: string;
};

export const PhoneForm = ({
    user,
    onSubmitPhone,
    product,
}: {
    user: IUserDto;
    onSubmitPhone: () => void;
    product: ProductDto;
}) => {
    const { openMessageConsultation } = useCreateConsultation(user.UID);
    const isSubmitting = useAtomValue(isSubmittingConsultationAtom);

    const [phoneNumber, setPhoneNumber] = useAtom(phoneNumberAtom);

    // Setting initial draft state. Draft-state is used for validation,
    // and this will otherwise only be set when changing phone number
    useEffect(() => {
        if (phoneNumber == null) {
            setPhoneNumber(user.PhoneNumber);
        }
    }, []);

    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
    } = useForm<FormData>({
        mode: 'onBlur',
    });

    // we reset the form when the draftState is hydrated (on refresh)
    useEffect(() => {
        reset({ phoneNumber });
    }, [phoneNumber]);

    function onSubmit() {
        if (product.IsVideo) {
            onSubmitPhone();
            return;
        }

        openMessageConsultation().catch((e) => {
            let additional;
            let error: Error;
            if (e instanceof Error) {
                error = e;
            } else {
                additional = e;
            }
            Log.error('Failed to open message consultation', additional, error);
        });
    }

    return (
        <Card alwaysCard={false}>
            <h2 className="mb-6">{R.PhoneFormHeader}</h2>
            <div className="mb-4">{R.PhoneFormDescription}</div>

            <form onSubmit={handleSubmit(onSubmit)}>
                <InputDeprecated
                    labelClassName="!mb-1"
                    label={R.PhoneNumberLabel}
                    type="tel"
                    {...register('phoneNumber', {
                        required: R.PhoneRequired,
                        validate: {
                            isValid: (value) =>
                                isPhoneNumber(value) || R.InvalidPhoneNumber,
                        },
                        onChange(e) {
                            const value = e.currentTarget.value;
                            setPhoneNumber(value);
                        },
                    })}
                    maxLength={20}
                    error={errors.phoneNumber?.message}
                />

                <div className="flex w-full flex-row justify-end">
                    <Button
                        variant="primary"
                        isSubmitting={isSubmitting}
                        type="submit"
                        className="ns:w-auto mt-4 w-full min-w-44"
                    >
                        {product.IsVideo ? R.Confirm : R.ConfirmAndSend}
                    </Button>
                </div>
            </form>
        </Card>
    );
};
